import React from 'react';
import Icon from '../../../components/AppIcon';

const VerificationProgress = ({ documents, overallStatus }) => {
  const getProgressPercentage = () => {
    const completedDocs = Object.values(documents)?.filter(doc => doc?.status === 'completed')?.length;
    return Math.round((completedDocs / Object.keys(documents)?.length) * 100);
  };

  const getOverallStatusInfo = () => {
    switch (overallStatus) {
      case 'completed':
        return {
          icon: 'CheckCircle',
          color: 'text-success',
          bgColor: 'bg-success/10',
          title: 'Verification Complete',
          description: 'All documents have been verified successfully'
        };
      case 'pending':
        return {
          icon: 'Clock',
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          title: 'Verification in Progress',
          description: 'Your documents are being reviewed'
        };
      case 'rejected':
        return {
          icon: 'XCircle',
          color: 'text-error',
          bgColor: 'bg-error/10',
          title: 'Action Required',
          description: 'Some documents need to be resubmitted'
        };
      default:
        return {
          icon: 'Upload',
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          title: 'Upload Required',
          description: 'Please upload all required documents'
        };
    }
  };

  const statusInfo = getOverallStatusInfo();
  const progressPercentage = getProgressPercentage();

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Verification Progress</h2>
        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${statusInfo?.bgColor}`}>
          <Icon name={statusInfo?.icon} size={16} className={statusInfo?.color} />
          <span className={`text-sm font-medium ${statusInfo?.color}`}>
            {progressPercentage}% Complete
          </span>
        </div>
      </div>
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-foreground">Overall Progress</span>
          <span className="text-sm text-muted-foreground">{progressPercentage}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
      <div className={`p-4 rounded-lg ${statusInfo?.bgColor} mb-6`}>
        <div className="flex items-start space-x-3">
          <Icon name={statusInfo?.icon} size={20} className={`${statusInfo?.color} flex-shrink-0 mt-0.5`} />
          <div>
            <h3 className={`font-medium ${statusInfo?.color}`}>{statusInfo?.title}</h3>
            <p className={`text-sm ${statusInfo?.color} opacity-80`}>{statusInfo?.description}</p>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-foreground">Document Status</h3>
        {Object.entries(documents)?.map(([key, doc]) => {
          const getDocIcon = () => {
            switch (doc?.status) {
              case 'completed':
                return { name: 'CheckCircle', color: 'text-success' };
              case 'pending':
                return { name: 'Clock', color: 'text-warning' };
              case 'rejected':
                return { name: 'XCircle', color: 'text-error' };
              default:
                return { name: 'Circle', color: 'text-muted-foreground' };
            }
          };

          const docIcon = getDocIcon();

          return (
            <div key={key} className="flex items-center justify-between py-2">
              <div className="flex items-center space-x-3">
                <Icon name={docIcon?.name} size={16} className={docIcon?.color} />
                <span className="text-sm text-foreground">{doc?.title}</span>
              </div>
              <span className={`text-xs font-medium capitalize ${docIcon?.color}`}>
                {doc?.status || 'Required'}
              </span>
            </div>
          );
        })}
      </div>
      {overallStatus === 'completed' && (
        <div className="mt-6 p-4 bg-success/10 rounded-lg border border-success/20">
          <div className="flex items-center space-x-3">
            <Icon name="Shield" size={20} className="text-success" />
            <div>
              <h4 className="text-sm font-medium text-success">Identity Verified</h4>
              <p className="text-xs text-success/80">
                Your account is now fully verified and ready for transactions
              </p>
            </div>
          </div>
        </div>
      )}
      {overallStatus === 'rejected' && (
        <div className="mt-6 p-4 bg-error/10 rounded-lg border border-error/20">
          <div className="flex items-center space-x-3">
            <Icon name="AlertTriangle" size={20} className="text-error" />
            <div>
              <h4 className="text-sm font-medium text-error">Action Required</h4>
              <p className="text-xs text-error/80">
                Please review and resubmit the rejected documents
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationProgress;